import { Vector3, type ArcRotateCamera } from "@babylonjs/core";

export interface Offset3 {
  x: number;
  y: number;
  z: number;
}

/** Camera position/target, both expressed as offsets from a subject's current position — reusable regardless of where that subject is standing. */
export interface CameraFraming {
  offset: Offset3;
  targetOffset: Offset3;
}

interface Transition {
  fromPos: Vector3;
  toPos: Vector3;
  fromTarget: Vector3;
  toTarget: Vector3;
  elapsedMs: number;
  durationMs: number;
}

/**
 * Smoothly moves the main ArcRotateCamera to vignette-specific framings and
 * back to a default idle framing. "Smooth" is a straightforward eased lerp
 * over a fixed duration, driven by `update(deltaMs)` from the render loop.
 *
 * Known simplification: doesn't detach `camera.attachControl` during a
 * cinematic, so a user dragging the mouse mid-vignette can fight the
 * transition. Acceptable for Phase 3's validation purposes; worth revisiting
 * once vignettes are a frequent, expected part of normal viewing.
 */
export class CinematicCameraRig {
  private readonly camera: ArcRotateCamera;
  private readonly defaultPosition: Vector3;
  private readonly defaultTarget: Vector3;
  private transition: Transition | null = null;

  /**
   * `defaultPosition`/`defaultTarget` are fixed absolute points (the
   * island's normal establishing shot) — unlike `frameOn`'s offset-based
   * framing, "default" isn't relative to Johnny, since he may end up
   * standing anywhere and the idle view should stay a stable, predictable
   * island shot regardless.
   */
  constructor(camera: ArcRotateCamera, defaultPosition: Vector3, defaultTarget: Vector3) {
    this.camera = camera;
    this.defaultPosition = defaultPosition;
    this.defaultTarget = defaultTarget;
  }

  frameOn(subjectPosition: Vector3, framing: CameraFraming, transitionMs = 1200): void {
    const toPos = subjectPosition.add(new Vector3(framing.offset.x, framing.offset.y, framing.offset.z));
    const toTarget = subjectPosition.add(
      new Vector3(framing.targetOffset.x, framing.targetOffset.y, framing.targetOffset.z)
    );
    this.beginTransition(toPos, toTarget, transitionMs);
  }

  returnToDefault(transitionMs = 1200): void {
    this.beginTransition(this.defaultPosition, this.defaultTarget, transitionMs);
  }

  /** Call every frame; a no-op once any in-flight transition has completed. */
  update(deltaMs: number): void {
    if (!this.transition) return;
    const tr = this.transition;
    tr.elapsedMs += deltaMs;
    const t = Math.min(1, tr.elapsedMs / tr.durationMs);
    const eased = easeInOutCubic(t);

    const pos = Vector3.Lerp(tr.fromPos, tr.toPos, eased);
    const target = Vector3.Lerp(tr.fromTarget, tr.toTarget, eased);
    this.camera.setPosition(pos);
    this.camera.setTarget(target);

    if (t >= 1) this.transition = null;
  }

  private beginTransition(toPos: Vector3, toTarget: Vector3, durationMs: number): void {
    this.transition = {
      fromPos: this.camera.position.clone(),
      toPos,
      fromTarget: this.camera.target.clone(),
      toTarget,
      elapsedMs: 0,
      durationMs,
    };
  }
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
